import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useContext } from "react";
import { styled } from "@mui/material";
import { Field, FormikProvider, FormikValues, useFormik } from "formik";
import * as Yup from "yup";
import Button from "../../../../customs/Button";
import Input from "../../../../customs/FormInput";
import Select from "../../../../customs/SelectInput";
import CustomTextArea from "../../../../customs/textarea";
import apiCall from "../../../../utils/apiCall";
import { NotificationContext } from "../../../../providers/notification";
import { reOrderFetchedData } from "../../../../hooks/FetchData";

interface ScoreTypeData {
  Name: number;
  ScoreTypeId: string;
}
interface CreateScorePayload {
  ScoreId:number;
  Score: number;
  ScoreTypeId: number;
  Description: string;
  ScoreTypeName:string;
}

interface Payload {
  RouteId: number;
  MinYears: number;
  MaxYears: number;
  Score: number;
  ActiveStatus: boolean;
}

interface ComponentProps {
  Score: number;
  handleClose: () => void;
  id: number;
  maxYear: number;
  minYear: number;
  activeStatus: boolean;
  route: number;
}

interface Routes {
  RouteName: string;
  RouteId: number;
};

interface RouteData {
  Data: Routes[];
};

interface RoutesResponse {
  data: RouteData;
};

const EditScoreContent: React.FC<ComponentProps> = ({ id, handleClose, Score, maxYear, minYear, route, activeStatus }) => {
 
  const { showNotification } = useContext(NotificationContext);
  const queryClient = useQueryClient();

  const GetAllScoreTypes = async (): Promise<any> => {
    return await apiCall().get(`/SetUp/GetAllScoreTypes`);
  };

  const updateHrTrainingScore = async (payload: FormData) => {
    return await apiCall().put(`/SetUp/UpdateCoHRScore?id=${id}`, payload);
  };

  const updateHrTrainingScoreMutation = useMutation(["update-hr-training-score"], updateHrTrainingScore, {
    retry: 0,
  });

  const updateHrTrainingScoreHandler = async (values: FormikValues, resetForm: () => void) => {
    const formData = new FormData();
    formData.append('RouteId', values?.route);
    formData.append('MinYears', values?.minYear);
    formData.append('MaxYears', values?.maxYear);
    formData.append('Score', values?.score);
    formData.append('ActiveStatus', values?.activeStatus === 0 ? 'true' : 'false');

    try {
      await updateHrTrainingScoreMutation.mutateAsync(formData, {
        onSuccess: () => {
          showNotification({
            message: "Score created successful",
            type: "success",
          });
          queryClient.refetchQueries(["get-AllScore"]);
          handleClose();
        },
      });
    } catch (error: any) {
      showNotification({
        message: error?.response?.data?.Message || error?.message,
        type: "error",
      });
    }
  };

  const validationSchema = Yup.object().shape({
    score: Yup.number().required("score is required"),
    route: Yup.number().required("route is required"),
    maxYear: Yup.number().required("maximum year is required"),
    minYear: Yup.number().required("minimum year is required"),
    activeStatus: Yup.boolean().required("active status is required")
  });

  const formik = useFormik<FormikValues>({
    initialValues: {
      route: route ? route : 0,
      score: Score ? Score : "",
      maxYear: maxYear ? maxYear : 0,
      minYear: minYear ? minYear : 0,
      activeStatus: activeStatus || true
    },
    onSubmit: (values, { resetForm }) => {
      updateHrTrainingScoreHandler(values, resetForm);
    },
    validationSchema,
  });

  const getAllRoutes = async (): Promise<RoutesResponse> => {
    return (await apiCall().get('/Routes/GetAllRoute'))
  };

  const { data, isLoading, isError, error } = useQuery(['get-all-route'], getAllRoutes, {
    enabled: true,
    keepPreviousData: true,
    retry: 0
  });

  const routeOption = data?.data?.Data && data?.data?.Data?.length > 0 ? (
   reOrderFetchedData(data?.data?.Data).map((route : Routes ) => (
      <option value={route?.RouteId} key = {route?.RouteId} >
          {route?.RouteName}
      </option>
    ))
    ) : (
      <option disabled>
          {isLoading ? 'loading....' : isError ? 'error': ''}
      </option>
  )

  const activeData = [
    {Id: 0, Status: 'Active'},
    {Id: 1, Status: 'Inactive'}
  ]

  const activeOption =
    activeData.map((data) => (
      <option value={data?.Id} key={data?.Id}>
          {data?.Status}
      </option>
  ))

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit}>
        <FormWrapper>
          <Field 
            name='route'
            type='text'
            as={Select}
            label='Route'
            options= {routeOption}
            placeholder='Select'
          />
          <Field 
            name='score'
            type='number'
            as={Input}
            label='Score'
            placeholder='Score'
          />
          <Field 
            name='minYear'
            type='number'
            as={Input}
            label='Min Year'
            placeholder='Min year'
          />
          <Field 
            name='maxYear'
            type='number'
            as={Input}
            label='Max Year'
            placeholder='Max Year'
          />
          <Field 
            name='activeStatus'
            type='text'
            as={Select}
            label='Active Status'
            options={activeOption}
            placeholder='Choose Status'
          />
        </FormWrapper>
        <ButtonWrapper>
          <ButtonContainer>
            <StyledButton text="Cancel" onClick={handleClose} />
            <StyledButton
              disabled={updateHrTrainingScoreMutation.isLoading}
              text={updateHrTrainingScoreMutation.isLoading ? "Updating..." : "Update"}
              onClick={formik.handleSubmit}
              type="submit"
            />
          </ButtonContainer>
        </ButtonWrapper>
      </form>
    </FormikProvider>
  );
};

export default EditScoreContent;

const FormWrapper = styled("div")`
  margin-inline: 3.5rem;
`;

const ButtonWrapper = styled("div")`
  display: flex;
  justify-content: flex-end;
  margin-block: 2.5rem;
  padding-inline: 3.5rem;
`;

const ButtonContainer = styled("div")`
  display: flex;
  gap: 2rem;
`;

const StyledButton = styled(Button)`
  padding-block: 1rem;
  padding-inline: 2rem;
`;
